import React from 'react';
import ProductCardProps from './types/ProductCardProps';
import StockStatus from '../StockStatus/StockStatus';
import { translate } from '../../Services/translation';
import ResponsiveImage from '../ResponsiveImage/ResponsiveImage';
import ProductTag from '../ProductTags/ProductTag';
import ProductCardBuyButton from './ProductCardBuyButton';
import clsx from 'clsx';
import Icon from '../Icon';
import { formatIncomingStocksString } from '../NavisionStock/services/NavisionStockService';

const ProductCard = ({
  product,
  isVariantListing,
  lazyLoadImage,
  navisionPrice,
  showBothPrices,
  navisionStock,
  currentPriceToggleType,
  isInBuyableState,
}: ProductCardProps) => {
  const defaultPriceSection = (
    <>
      {product.price.formattedCampaignPrice ? (
        <div className="flex flex-col">
          <h4 className="text-error-500">
            {product.price.formattedCampaignPrice}
          </h4>
          <p className={clsx('line-through', 'text-sm', 'text-gray-500')}>
            <p className="text-sm text-gray-500">
              {product.price.formattedPrice &&
                translate('product.recommended-price') +
                  ': ' +
                  product.price.formattedPrice}
            </p>
          </p>
        </div>
      ) : (
        product.price.formattedPrice && (
          <div className="price-wrapper">
            {product.navPriceServiceEnabled ? (
              <h4>{product.price.formattedPrice}</h4>
            ) : (
              <>
                <p className="text-sm text-gray-500">
                  {translate('product.recommended-price-ex') + ': '}
                </p>
                <h4>{product.price.formattedPrice}</h4>
              </>
            )}
          </div>
        )
      )}
      {product.productFee &&
        product.productFee.length > 0 &&
        product.productFee.map(
          (fee) =>
            fee.priceModel.formattedPrice && (
              <p
                className={clsx('text-sm', 'text-gray-500')}
                key={fee.feeTypeName}
              >
                {translate('product.fee')} ({fee.feeTypeName}){': '}
                {fee.priceModel.formattedPrice}
              </p>
            )
        )}
    </>
  );

  return (
    <div
      className={clsx(
        'flex',
        'flex-col',
        'bg-white',
        'rounded',
        'h-full',
        'max-w-full',
        'm-w-0',
        'border-solid',
        'border',
        'border-gray-100'
      )}
    >
      <a
        href={product.url}
        className={clsx(
          'flex',
          'bg-gray-100',
          'rounded-t',
          'aspect-square',
          'relative',
          'overflow-hidden',
          '[&_img]:aspect-square',
          '[&_img]:object-cover',
          '[&_img]:h-full',
          '[&_img]:w-full'
        )}
      >
        {product.images && product.images.length > 0 && (
          <ResponsiveImage
            {...product.images[0]}
            lazyLoad={lazyLoadImage}
          />
        )}
        {product.tags?.length ? (
          <div
            className={clsx(
              'absolute',
              'top-0',
              'left-0',
              'flex',
              'flex-wrap',
              'gap-2'
            )}
          >
            {product.tags.map((tag, idx) => (
              <ProductTag
                tagSize="medium"
                id={tag.id}
                key={tag.id}
                className={clsx(idx !== 0 && 'rounded-b')}
              >
                {tag.name}
              </ProductTag>
            ))}
          </div>
        ) : null}
      </a>
      <div
        className={clsx(
          'flex-col',
          'flex',
          'gap-2',
          'flex-1',
          'rounded-b',
          'pt-1',
          'px-3',
          'pb-3',
          'border-solid',
          'border-t',
          'border-gray-100',
          'lg:pt-2',
          'lg:px-4',
          'lg:pb-4'
        )}
      >
        <div className={clsx('flex-1', 'gap-1')}>
          {product.brand && (
            <p className={clsx('text-md', 'font-bold', 'text-gray-600')}>
              {product.brand}
            </p>
          )}
          <a
            className={clsx('text-md', 'font-bold', 'lg:text-lg')}
            href={product.url}
          >
            {product.name}
          </a>
          {product.id && (
            <p className={clsx('text-md', 'text-gray-600')}>
              {translate('product.articleno')} {product.id}
            </p>
          )}
          {product.hasOtherVariants ? (
            <div className="flex">
              <p className="mb-1 mt-1 rounded bg-gray-100 pl-2 pr-2">
                {translate('product.multiple-variants')}
              </p>
            </div>
          ) : (
            <></>
          )}
        </div>
        {currentPriceToggleType &&
          product.showNavStock &&
          navisionStock?.formattedExpectedArrivalDate && (
            <div className="flex-1 text-md">
              {isInBuyableState &&
                formatIncomingStocksString(
                  navisionStock.totalIncomingQuantity,
                  navisionStock.firstShipmentQuantity,
                  navisionStock.formattedExpectedArrivalDate
                )}
            </div>
          )}
        <div className="flex flex-col">
          {isInBuyableState ? (
            <>
              {showBothPrices ? (
                <>
                  <h4>
                    {(isInBuyableState && navisionPrice?.formattedPrice) ??
                      translate('product.placeholder.fetching.price')}
                  </h4>
                  <p className={clsx('text-sm', 'text-gray-500')}>
                    {product.price.formattedPrice &&
                      translate('product.recommended-price') +
                        ' : ' +
                        product.price.formattedPrice}
                  </p>
                  {product.productFee &&
                    product.productFee.length > 0 &&
                    product.productFee.map(
                      (fee) =>
                        fee.priceModel.formattedPrice && (
                          <p
                            className={clsx('text-sm', 'text-gray-500')}
                            key={fee.feeTypeName}
                          >
                            {translate('product.fee')} ({fee.feeTypeName})
                            {': '}
                            {fee.priceModel.formattedPrice}
                          </p>
                        )
                    )}
                </>
              ) : (
                defaultPriceSection
              )}
            </>
          ) : (
            <p className={clsx('text-sm', 'text-gray-500')}>
              {product.price.formattedPrice &&
                translate('product.recommended-price') +
                  ' : ' +
                  product.price.formattedPrice}
            </p>
          )}
        </div>

        {isInBuyableState && (
          <ProductCardBuyButton
            product={product}
            isVariantListing={isVariantListing}
            className={clsx(
              'lg:[&_input]:py-2',
              'lg:[&_input]:px-4',
              'lg:[&_input]:text-xl'
            )}
            label={<Icon name="shopping-cart" className="h-5 w-5" />}
          />
        )}
        {product.showNavStock ? (
          // Render stock statuses from Navision if available and enabled
          isInBuyableState &&
          navisionStock &&
          navisionStock.stockStatuses ? (
            <>
              {/* Render the first stock status from Navision */}
              {navisionStock.stockStatuses[0] && (
                <StockStatus
                  {...navisionStock.stockStatuses[0].stockStatus}
                />
              )}

              {/* Render the second stock status from Navision */}
              {navisionStock?.stockStatuses[1] && (
                <StockStatus
                  {...navisionStock.stockStatuses[1].stockStatus}
                />
              )}
            </>
          ) : (
            // Render default stock status placeholder
            <>
              {isInBuyableState && (
                <StockStatus {...product.stockStatusPlaceholder} />
              )}
            </>
          )
        ) : (
          // Render default stock status (Litium stock) if Navision is unreachable or disabled
          <>
            {isInBuyableState && <StockStatus {...product.stockStatus} />}
          </>
        )}
      </div>
    </div>
  );
};

export default ProductCard;
